import React, { useState } from 'react';
import {
  Card,
  Typography,
  Form,
  Input,
  Button,
  Upload,
  Row,
  Col,
  Space,
  Divider,
  ColorPicker,
  Select,
  Switch,
  message,
  Alert,
  Tabs,
} from 'antd';
import {
  UploadOutlined,
  EyeOutlined,
  SaveOutlined,
  ReloadOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { BRAND_COLORS, LOGO_SIZES } from '../constants';
import type { BrandingSettings as BrandingSettingsType } from '../types';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const BrandingSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [previewMode, setPreviewMode] = useState(false);
  const [settings, setSettings] = useState<Partial<BrandingSettingsType>>({
    primaryColor: '#1890ff',
    secondaryColor: '#722ed1',
    successColor: '#52c41a',
    warningColor: '#faad14',
    errorColor: '#f5222d',
    backgroundColor: '#f0f2f5',
    surfaceColor: '#ffffff',
    textPrimaryColor: '#262626',
    textSecondaryColor: '#8c8c8c',
    logoSize: 'medium',
    fontFamily: 'Inter, sans-serif',
  });

  // Font options
  const fontOptions = [
    { value: 'Inter, sans-serif', label: 'Inter (Default)' },
    { value: 'Roboto, sans-serif', label: 'Roboto' },
    { value: 'Open Sans, sans-serif', label: 'Open Sans' },
    { value: 'Lato, sans-serif', label: 'Lato' },
    { value: 'Montserrat, sans-serif', label: 'Montserrat' },
    { value: 'Poppins, sans-serif', label: 'Poppins' },
    { value: 'Source Sans Pro, sans-serif', label: 'Source Sans Pro' },
    { value: 'system-ui, sans-serif', label: 'System Default' },
  ];

  // Logo upload configuration
  const logoUploadProps: UploadProps = {
    name: 'logo',
    listType: 'picture-card',
    maxCount: 1,
    beforeUpload: (file) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/svg+xml';
      if (!isJpgOrPng) {
        message.error('You can only upload JPG, PNG, or SVG files!');
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('Image must be smaller than 2MB!');
        return false;
      }
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setSettings(prev => ({
          ...prev,
          logoUrl: e.target?.result as string,
        }));
      };
      reader.readAsDataURL(file);
      
      return false; // Prevent default upload
    },
  };

  // Favicon upload configuration
  const faviconUploadProps: UploadProps = {
    name: 'favicon',
    listType: 'picture-card',
    maxCount: 1,
    beforeUpload: (file) => {
      const isIcon = file.type === 'image/x-icon' || file.type === 'image/png';
      if (!isIcon) {
        message.error('You can only upload ICO or PNG files for favicon!');
        return false;
      }
      const isLt1M = file.size / 1024 / 1024 < 1;
      if (!isLt1M) {
        message.error('Favicon must be smaller than 1MB!');
        return false;
      }
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setSettings(prev => ({
          ...prev,
          faviconUrl: e.target?.result as string,
        }));
      };
      reader.readAsDataURL(file);
      
      return false;
    },
  };

  // Handle color change
  const handleColorChange = (colorKey: string, color: any) => {
    const colorValue = typeof color === 'string' ? color : color.toHexString();
    setSettings(prev => ({
      ...prev,
      [colorKey]: colorValue,
    }));
  };

  // Save settings
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const updatedSettings = {
        ...settings,
        ...values,
        updatedBy: 'current-user', // This would come from auth context
        updatedAt: new Date().toISOString(),
      };
      
      console.log('Saving branding settings:', updatedSettings);
      message.success('Branding settings saved successfully!');
    } catch (error) {
      message.error('Please fix the form errors before saving.');
    }
  };

  // Reset to defaults
  const handleReset = () => {
    const defaultSettings = {
      primaryColor: '#1890ff',
      secondaryColor: '#722ed1',
      successColor: '#52c41a',
      warningColor: '#faad14',
      errorColor: '#f5222d',
      backgroundColor: '#f0f2f5',
      surfaceColor: '#ffffff',
      textPrimaryColor: '#262626',
      textSecondaryColor: '#8c8c8c',
      logoSize: 'medium' as const,
      fontFamily: 'Inter, sans-serif',
    };
    
    setSettings(defaultSettings);
    form.resetFields();
    message.info('Settings reset to defaults');
  };

  // Preview component
  const renderPreview = () => (
    <Card
      style={{
        backgroundColor: settings.backgroundColor,
        color: settings.textPrimaryColor,
        fontFamily: settings.fontFamily,
      }}
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        {settings.logoUrl && (
          <img
            src={settings.logoUrl}
            alt="Logo Preview"
            style={{
              maxHeight: LOGO_SIZES.find(s => s.value === settings.logoSize)?.height || 60,
              marginBottom: 16,
            }}
          />
        )}
        
        <Title
          level={2}
          style={{
            color: settings.primaryColor,
            fontFamily: settings.fontFamily,
            margin: '16px 0',
          }}
        >
          Fit UI Manager
        </Title>
        
        <Paragraph style={{ color: settings.textSecondaryColor }}>
          This is how your branding will appear throughout the application.
        </Paragraph>
        
        <Space>
          <Button type="primary" style={{ backgroundColor: settings.primaryColor, borderColor: settings.primaryColor }}>
            Primary Button
          </Button>
          <Button style={{ color: settings.secondaryColor, borderColor: settings.secondaryColor }}>
            Secondary Button
          </Button>
        </Space>
        
        <div style={{ marginTop: 20 }}>
          <Alert message="Success" type="success" style={{ backgroundColor: settings.successColor }} />
          <Alert message="Warning" type="warning" style={{ backgroundColor: settings.warningColor, marginTop: 8 }} />
          <Alert message="Error" type="error" style={{ backgroundColor: settings.errorColor, marginTop: 8 }} />
        </div>
      </div>
    </Card>
  );

  const tabItems = [
    {
      key: 'logos',
      label: 'Logos & Assets',
      children: (
        <Row gutter={24}>
          <Col span={12}>
            <Card title="Main Logo" size="small">
              <Form.Item name="logoSize" label="Logo Size">
                <Select
                  value={settings.logoSize}
                  onChange={(value) => setSettings(prev => ({ ...prev, logoSize: value }))}
                >
                  {LOGO_SIZES.map(size => (
                    <Option key={size.value} value={size.value}>
                      {size.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              
              <Upload {...logoUploadProps}>
                {settings.logoUrl ? (
                  <img src={settings.logoUrl} alt="logo" style={{ width: '100%', maxHeight: 100, objectFit: 'contain' }} />
                ) : (
                  <div>
                    <PictureOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                    <div>Upload Logo</div>
                  </div>
                )}
              </Upload>
              
              <Text type="secondary" style={{ fontSize: 12 }}>
                Recommended: PNG, SVG, or JPG. Max 2MB.
              </Text>
            </Card>
          </Col>
          
          <Col span={12}>
            <Card title="Favicon" size="small">
              <Upload {...faviconUploadProps}>
                {settings.faviconUrl ? (
                  <img src={settings.faviconUrl} alt="favicon" style={{ width: 32, height: 32 }} />
                ) : (
                  <div>
                    <PictureOutlined style={{ fontSize: 24, color: '#d9d9d9' }} />
                    <div>Upload Favicon</div>
                  </div>
                )}
              </Upload>
              
              <Text type="secondary" style={{ fontSize: 12 }}>
                16x16 or 32x32 pixels. ICO or PNG format.
              </Text>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      key: 'colors',
      label: 'Colors',
      children: (
        <Row gutter={24}>
          {BRAND_COLORS.map(color => (
            <Col key={color.key} span={8} style={{ marginBottom: 16 }}>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <Text strong>{color.name}</Text>
                  <div style={{ margin: '8px 0' }}>
                    <ColorPicker
                      value={settings[color.key as keyof typeof settings] as string || color.default}
                      onChange={(colorValue) => handleColorChange(color.key, colorValue)}
                      showText
                      size="large"
                    />
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Default: {color.default}
                  </Text>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      ),
    },
    {
      key: 'typography',
      label: 'Typography',
      children: (
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item name="fontFamily" label="Font Family">
              <Select
                value={settings.fontFamily}
                onChange={(value) => setSettings(prev => ({ ...prev, fontFamily: value }))}
              >
                {fontOptions.map(font => (
                  <Option key={font.value} value={font.value}>
                    <span style={{ fontFamily: font.value }}>{font.label}</span>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            
            <Alert
              message="Font Loading"
              description="Google Fonts will be automatically loaded. System fonts load faster."
              type="info"
              style={{ marginTop: 16 }}
            />
          </Col>
          
          <Col span={12}>
            <div style={{ fontFamily: settings.fontFamily, padding: 20, border: '1px solid #d9d9d9', borderRadius: 8 }}>
              <Title level={3} style={{ fontFamily: settings.fontFamily }}>Sample Heading</Title>
              <Paragraph style={{ fontFamily: settings.fontFamily }}>
                This is how your text will appear with the selected font family. 
                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
              </Paragraph>
              <Text strong style={{ fontFamily: settings.fontFamily }}>Bold Text Example</Text>
              <br />
              <Text type="secondary" style={{ fontFamily: settings.fontFamily }}>Secondary Text Example</Text>
            </div>
          </Col>
        </Row>
      ),
    },
    {
      key: 'custom',
      label: 'Custom CSS',
      children: (
        <Row gutter={24}>
          <Col span={16}>
            <Form.Item
              name="customCss"
              label="Custom CSS"
              help="Add custom CSS to override default styles. Use with caution."
            >
              <TextArea
                rows={12}
                placeholder={`/* Custom CSS Example */
.ant-btn-primary {
  border-radius: 8px;
}

.custom-header {
  background: linear-gradient(90deg, #1890ff, #722ed1);
}`}
              />
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Alert
              message="CSS Guidelines"
              description={
                <ul style={{ paddingLeft: 16, margin: 0 }}>
                  <li>Use specific selectors to avoid conflicts</li>
                  <li>Test changes in preview mode</li>
                  <li>Backup your settings before major changes</li>
                  <li>Consider responsive design</li>
                </ul>
              }
              type="warning"
            />
          </Col>
        </Row>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3}>Branding Settings</Title>
            <Text type="secondary">
              Customize your application's visual identity with logos, colors, and typography.
            </Text>
          </Col>
          
          <Col>
            <Space>
              <Button
                icon={<EyeOutlined />}
                onClick={() => setPreviewMode(!previewMode)}
                type={previewMode ? 'primary' : 'default'}
              >
                {previewMode ? 'Hide Preview' : 'Show Preview'}
              </Button>
              
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Reset to Defaults
              </Button>
              
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={24} style={{ marginTop: 16 }}>
        <Col span={previewMode ? 16 : 24}>
          <Card>
            <Form form={form} layout="vertical">
              <Tabs items={tabItems} />
            </Form>
          </Card>
        </Col>
        
        {previewMode && (
          <Col span={8}>
            <div style={{ position: 'sticky', top: 16 }}>
              <Title level={4}>Live Preview</Title>
              {renderPreview()}
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default BrandingSettings; 